import { calcularValorVigenteOrcamento, etapaEstimativaLinha, ROTULO_ETAPA_VIGENTE } from './colunaVigentePolicy';
import { calcularFechamentoExterior } from './fechamentoExteriorPolicy';
import { calcularRemessaExteriorEstimativa, linhaElegivelRemessaExterior, valorRemessaExteriorPainel } from './remessaExteriorEstimativaPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';

// Item 12 (26/09): no orçamento INTERNACIONAL a barra TOTAL ACCESS mostra o MESMO valor do painel
// (PainelOrcamento → valorRemessaExteriorPainel). A prioridade é uma só; aqui só montamos, ao
// vivo, os mesmos campos que a API devolve na listagem:
//   totalRealAtualBRL / fechamento_real_ativo_atual → prévia do fechamento (card "Total atual");
//   totalEstimadoAtualBRL / remessa_estimativa_ativa_atual → espelho de OrcamentoRemessaEstimativa;
//   valorTotal → coluna vigente (calcularValorVigenteOrcamento).

const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
    if (valor === null || valor === undefined || String(valor).trim() === '') return null;
    const texto = String(valor).trim();
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : null;
};

// Espelho de accessapi OrcamentoRemessaEstimativa (totalEstimadoAtualBRL):
// round(round(valorTotal vigente × câmbio, 2) + encargos estimados das linhas exterior ABERTAS
// na etapa que a coluna vigente usa, 2). Ledger concluído não soma estimativa.
export function calcularTotalEstimadoAtualBrl(orcamento = {}, fornecedorAccess = null, opcoesImposto = {}) {
    const vigente = calcularValorVigenteOrcamento(orcamento, fornecedorAccess, opcoesImposto);
    const moeda = String(orcamento.moeda || 'real').toLowerCase();
    const cambio = moeda === 'real' ? 1 : numero(orcamento.cambio);
    let ativa = false;
    let adicionais = 0;
    // Cada linha na coluna vigente do FORNECEDOR dela (A01, 28/09), a mesma do valorTotal.
    (orcamento.orcamentoitem || []).flatMap((grupo) => grupo.subitem || []).forEach((linha, i) => {
        // Opção (D-0113): não vai ser paga, então não tem encargo de remessa no total.
        if (ehSomenteVisualizacao(linha) || !linhaElegivelRemessaExterior(linha, fornecedorAccess)) return;
        if (calcularFechamentoExterior(linha, fornecedorAccess).completo) return;
        // Contrato v2 A (planilha): linha sem principal ainda cobra envio + NF; vale a etapa da coluna vigente
        // do fornecedor dela (Fechamento sem estimativa: a última etapa com taxas).
        let etapaAtual = etapaEstimativaLinha(linha, vigente.etapas[i]);
        if (!etapaAtual) {
            const calculado = calcularRemessaExteriorEstimativa(linha, orcamento, fornecedorAccess, { exato: true });
            const etapaVigente = vigente.etapas[i];
            etapaAtual = etapaVigente === 'Negociado' ? 'neg' : etapaVigente === 'PreEvento' ? 'pre'
                : (calculado.neg.adicional_brl > 0 ? 'neg' : (calculado.pre.adicional_brl > 0 ? 'pre' : null));
            if (!etapaAtual || !(calculado[etapaAtual].adicional_brl > 0)) return;
        }
        ativa = true;
        adicionais += calcularRemessaExteriorEstimativa(linha, orcamento, fornecedorAccess, { exato: true })[etapaAtual].adicional_brl;
    });
    // Valor exato em reais (contrato 02/10, regra 3): parte da soma dos R$ das linhas (vigente.totalBrl),
    // nunca do valorTotal na moeda × câmbio.
    // Contrato v2 B: soma com precisão total (a tela formata com 2 casas).
    const total = cambio !== null && cambio > 0 ? vigente.totalBrl + adicionais : null;
    return { total, ativa, etapa: vigente.etapa, valorTotal: vigente.total, valorTotalBrl: vigente.totalBrl };
}

const FONTES = {
    real_atual: 'Total atual do Fechamento exterior (pagamentos informados + estimativa das linhas abertas)',
    estimativa_atual: 'Total com a estimativa de remessa do exterior (encargos em reais)',
    valor_total: 'Soma das linhas na última coluna preenchida',
};

// Valor da barra = valorRemessaExteriorPainel sobre os números da tela (mesma regra do painel).
export function valorPainelOrcamentoAoVivo(orcamento = {}, fornecedorAccess = null, opcoesImposto = {}) {
    const estimado = calcularTotalEstimadoAtualBrl(orcamento, fornecedorAccess, opcoesImposto);
    const painel = valorRemessaExteriorPainel({
        ...orcamento,
        valorTotal: estimado.valorTotal,
        valorTotalBRL: estimado.valorTotalBrl,
        totalEstimadoAtualBRL: estimado.total,
        remessa_estimativa_ativa_atual: estimado.ativa,
    });
    return { ...painel, etapa: estimado.etapa, rotuloEtapa: ROTULO_ETAPA_VIGENTE[estimado.etapa], descricaoFonte: FONTES[painel.fonte] };
}

// Barra TOTAL ACCESS mostra o valor do painel sempre que o PAINEL aplica a regra da remessa do exterior
// (Total atual do fechamento ou estimativa com encargos — fonte ≠ valor_total), inclusive no evento
// nacional com linhas pagas no exterior, e sempre no internacional (valor em BRL). Nacional sem linha
// exterior devolve null e a barra mantém o "Valor total do orçamento" da coluna vigente (item 5).
export function valorPainelBarraTotalAccess(orcamento = {}, { internacional = false, fornecedorAccess = null, aplicaImposto, clienteEhChiesi } = {}) {
    const painel = valorPainelOrcamentoAoVivo(orcamento, fornecedorAccess, { aplicaImposto, clienteEhChiesi });
    return internacional || painel.fonte !== 'valor_total' ? painel : null;
}
